<script setup>
import { storeToRefs } from 'pinia'
import Illustration from 'assets/illustrations/illu-04.svg?url'
import Illustration2 from 'assets/illustrations/illu-02.svg?url'
import { useMessageStore } from '~/stores/messages'
import { useAuthStore } from '~/stores/auth'
import { signIn } from '~/composables/useMsal'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get Webview global state
const isWebview = useState('state_isWebview')

// Fetch auth store
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch history payment data
const bearerToken = useState('state_bearerToken')

// const { data: parkingPaymentHistoryItems, suspense: parkingPaymentHistorySuspense, isPlaceholderData: parkingPaymentHistoryPlaceholder } = useFetchParkingPaymentHistory(oid)
const { data: historyData, isPlaceholderData: parkingPaymentHistoryPlaceholder, isError: isHistoryError, error: historyError } = useFetchParkingPaymentHistory({ bearerToken })

// Prefetch data on the server side
/* onServerPrefetch(async () => {
    await parkingPaymentHistorySuspense()
}) */

watch(isHistoryError, () => {
    if (isHistoryError.value) {
        console.error('Error fetching parking payment history:', historyError.value)
    }
})
// Compute parking history
const hasParkingHistory = computed(() => historyData?.value?.parkingSessions?.length > 0)

// Trigger auto login if not logged in,  page is not webview and if on client
if (!isLoggedIn.value && import.meta.client && !isWebview.value) {
    // Fetch data
    const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

    // Prefetch data on the server side
    onServerPrefetch(async () => {
        await centerDetailSuspense()
    })

    await signIn({
        context: 'parking-history',
        state: 'ignoreOnBoarding',
        center: centerRoute.value,
        centerName: centerDetails.value?.centerName
    })
}
</script>

<template>
    <header class="parking-payment-history-overview-header">
        <ContentMainHeadline :content-main-headline-config="{ title: `_${staticKeys?.parkinghistory_headline}_` }" />
    </header>

    <section v-if="! isLoggedIn || parkingPaymentHistoryPlaceholder">
        <SkeletonPlaceholder :skeleton-placeholder-config="{ type: 'text', length: 70 }" />
        <br>
        <br>

        <DefaultContentBoxList>
            <DefaultContentBox v-for="index in 3" :key="index" class="skeleton-placeholder-container">
                <SkeletonPlaceholder :skeleton-placeholder-config="{ type: 'image', width: '100%', height: '68px' } " />
                <br>
                <br>
                <br>
                <br>
                <SkeletonPlaceholder :skeleton-placeholder-config="{ type: 'text', iterator: 1 }" />
                <br>
                <br>
                <br>
                <SkeletonPlaceholder :skeleton-placeholder-config="{ type: 'button' }" />
            </DefaultContentBox>
        </DefaultContentBoxList>
    </section>

    <template v-else>
        <template v-if="hasParkingHistory">
            <p>
                {{ staticKeys.parkingfirstpage_history_abstract }}
            </p>

            <DefaultContentBoxList>
                <DefaultContentBox
                    v-for="(parkingPaymentHistoryItem) in historyData?.parkingSessions"
                    :key="parkingPaymentHistoryItem?.id"
                >
                    <ParkingPaymentItem
                        :parking-payment-info="{
                            startDate: parkingPaymentHistoryItem.startDate,
                            duration: parkingPaymentHistoryItem?.durationText,
                            totalPrice: getFormattedPrice(parkingPaymentHistoryItem?.totalPrice?.amount, parkingPaymentHistoryItem?.totalPrice?.currency),
                            center: parkingPaymentHistoryItem?.center?.name,
                            toggle: !! parkingPaymentHistoryItem?.invoice,
                            pdfLink: parkingPaymentHistoryItem.invoice?.downloadLink
                        }"
                    />
                </DefaultContentBox>
            </DefaultContentBoxList>
        </template>

        <p v-else class="no-parking-history-container">
            <img
                :src="isHistoryError ? Illustration2 : Illustration" :alt="staticKeys?.parkinghistory_empty" width="220"
                height="251"
            >

            {{ isHistoryError ? staticKeys?.parkinghistory_error : staticKeys?.parkinghistory_empty }}
        </p>
    </template>
    <DevOnly>
        <pre>{{ historyData?.parkingSessions }}</pre>
    </DevOnly>
</template>

<style lang="scss">
.no-parking-history-container {
    text-align: center;
    max-width: 400px;
    margin: 0 auto;

    img {
        display: block;
        margin: 0 auto 20px;
    }
}
</style>
